import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button,
  Container, Stack, Link, IconButton, Checkbox, FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBackIosNew, CheckCircle, RadioButtonUnchecked, Bolt, FavoriteRounded, ChatBubbleRounded } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es } from 'date-fns/locale';
import { format, parse, subYears } from 'date-fns';
import { auth, db } from './firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  signOut,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import PullToRefresh from './PullToRefresh';

const DATE_FORMAT = 'yyyy-MM-dd';
const MIN_AGE = 13;
const MAX_AGE = 120;
const GEO_API_BASE = 'https://countriesnow.space/api/v0.1';
const GEO_ERROR_MESSAGE = 'No se pudo conectar con el servicio de ubicaciones. Podés escribir el dato manualmente.';

const getPasswordChecks = (pw) => ({
  length: pw.length >= 8,
  uppercase: /[A-Z]/.test(pw),
  number: /[0-9]/.test(pw),
  special: /[^A-Za-z0-9]/.test(pw),
});

const STRENGTH_LEVELS = [
  { label: '', color: 'rgba(255,255,255,0.1)' },
  { label: 'Muy débil', color: '#ff4d4d' },
  { label: 'Débil', color: '#ffa64d' },
  { label: 'Buena', color: '#ffe14d' },
  { label: 'Fuerte', color: '#00e676' },
];

const PASSWORD_REQUIREMENTS = [
  { key: 'length', label: 'Mínimo 8 caracteres' },
  { key: 'uppercase', label: 'Una letra mayúscula' },
  { key: 'number', label: 'Un número' },
  { key: 'special', label: 'Un carácter especial' },
];

const autocompleteSlotProps = {
  paper: {
    sx: {
      bgcolor: '#12181f',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      '& .MuiAutocomplete-option': { fontSize: '0.88rem' },
      '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(0,212,255,0.14)' },
    },
  },
};

const GoogleLogo = () => (
  <Box component="svg" viewBox="0 0 48 48" sx={{ width: 18, height: 18 }}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </Box>
);

// completeProfileFor: usuario que entró con Google y todavía no tiene perfil en Firestore.
const Auth = ({ completeProfileFor = null }) => {
  const [view, setView] = useState(completeProfileFor ? 'complete' : 'login');
  const isProfileForm = view === 'register' || view === 'complete';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [displayName, setDisplayName] = useState(completeProfileFor?.displayName || '');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [pais, setPais] = useState('');
  const [provincia, setProvincia] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  const statesRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const lastFetchedCountryRef = useRef('');
  const lastFetchedStateRef = useRef('');
  const countriesFetchedRef = useRef(false);
  const paisRef = useRef('');
  const provinciaRef = useRef('');

  const passwordChecks = useMemo(() => getPasswordChecks(password), [password]);
  const passwordScore = useMemo(
    () => Object.values(passwordChecks).filter(Boolean).length,
    [passwordChecks]
  );
  const isPasswordStrong = passwordScore === PASSWORD_REQUIREMENTS.length;
  const strengthInfo = STRENGTH_LEVELS[passwordScore];
  const passwordsMatch = confirmPassword.length === 0 || confirmPassword === password;

  const requiresProvincia = states.length > 0;

  // Cargar la lista de países desde CountriesNow al entrar a la vista de registro
  useEffect(() => {
    if (!isProfileForm || countriesFetchedRef.current) return;
    countriesFetchedRef.current = true;
    let cancelled = false;
    setCountriesLoading(true);
    fetch(`${GEO_API_BASE}/countries/positions`)
      .then((res) => {
        if (!res.ok) throw new Error('bad response');
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const names = (json?.data || [])
          .map((c) => c.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setCountries(names);
      })
      .catch(() => {
        if (cancelled) return;
        setGeoError(GEO_ERROR_MESSAGE);
        countriesFetchedRef.current = false; // permitir reintentar
      })
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isProfileForm]);

  const fetchCitiesForState = useCallback(async (countryName, stateName) => {
    const trimmedState = stateName.trim();
    if (trimmedState === lastFetchedStateRef.current) return;
    setCiudad('');
    setCityOptions([]);
    if (!trimmedState) {
      lastFetchedStateRef.current = '';
      return;
    }
    // Evita disparar peticiones destinadas a fallar cuando el texto escrito
    // no coincide con ninguna provincia/estado real ya cargado.
    const isKnownState = states.some((s) => s.toLowerCase() === trimmedState.toLowerCase());
    if (!isKnownState) return;
    lastFetchedStateRef.current = trimmedState;
    const requestId = ++citiesRequestRef.current;
    setCitiesLoading(true);
    try {
      const url = `${GEO_API_BASE}/countries/state/cities/q?country=${encodeURIComponent(countryName)}&state=${encodeURIComponent(trimmedState)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('bad response');
      const json = await res.json();
      if (requestId !== citiesRequestRef.current) return;
      setCityOptions(Array.isArray(json?.data) ? json.data : []);
    } catch {
      if (requestId === citiesRequestRef.current) setGeoError(GEO_ERROR_MESSAGE);
    } finally {
      if (requestId === citiesRequestRef.current) setCitiesLoading(false);
    }
  }, [states]);

  const fetchStatesForCountry = useCallback(async (countryName) => {
    const trimmedCountry = countryName.trim();
    if (trimmedCountry === lastFetchedCountryRef.current) return;
    setProvincia('');
    setCiudad('');
    setStates([]);
    setCityOptions([]);
    lastFetchedStateRef.current = '';
    if (!trimmedCountry) {
      lastFetchedCountryRef.current = '';
      return;
    }
    // Evita disparar peticiones destinadas a fallar cuando el texto escrito
    // no coincide con ningún país real ya cargado en la lista.
    const isKnownCountry = countries.length === 0
      || countries.some((c) => c.toLowerCase() === trimmedCountry.toLowerCase());
    if (!isKnownCountry) return;
    lastFetchedCountryRef.current = trimmedCountry;
    const requestId = ++statesRequestRef.current;
    setStatesLoading(true);
    try {
      const res = await fetch(`${GEO_API_BASE}/countries/states/q?country=${encodeURIComponent(trimmedCountry)}`);
      if (!res.ok) throw new Error('bad response');
      const json = await res.json();
      if (requestId !== statesRequestRef.current) return;
      const stateNames = (json?.data?.states || []).map((s) => s.name).filter(Boolean);
      if (stateNames.length > 0) {
        setStates(stateNames);
      } else {
        // País sin provincias/estados: traer sus ciudades directamente
        const citiesRes = await fetch(`${GEO_API_BASE}/countries/cities/q?country=${encodeURIComponent(trimmedCountry)}`);
        if (!citiesRes.ok) throw new Error('bad response');
        const citiesJson = await citiesRes.json();
        if (requestId !== statesRequestRef.current) return;
        setCityOptions(Array.isArray(citiesJson?.data) ? citiesJson.data : []);
      }
    } catch {
      if (requestId === statesRequestRef.current) setGeoError(GEO_ERROR_MESSAGE);
    } finally {
      if (requestId === statesRequestRef.current) setStatesLoading(false);
    }
  }, [countries]);

  const handleCountryChange = useCallback((countryName) => {
    setPais(countryName);
    fetchStatesForCountry(countryName);
  }, [fetchStatesForCountry]);

  const handleProvinciaChange = useCallback((provinciaName) => {
    setProvincia(provinciaName);
    fetchCitiesForState(pais, provinciaName);
  }, [pais, fetchCitiesForState]);

  const maxBirthDate = subYears(new Date(), MIN_AGE);
  const minBirthDate = subYears(new Date(), MAX_AGE);
  const selectedBirthDate = fechaNacimiento
    ? parse(fechaNacimiento, DATE_FORMAT, new Date())
    : null;

  const validateProfileFields = () => {
    if (!displayName || !username || !pais || !ciudad || !fechaNacimiento || (requiresProvincia && !provincia)) {
      return 'Por favor completa todos los campos';
    }
    return '';
  };

  const validateLegal = () => (
    acceptedLegal ? '' : 'Debes aceptar la Política de Privacidad, los Términos y los Estándares CSAE'
  );

  // Email y fecha de nacimiento van al documento privado (solo lo lee el dueño);
  // el perfil público lo puede leer cualquier usuario.
  const saveProfile = async (user, userEmail, userPhotoURL = null) => {
    await setDoc(doc(db, 'users', user.uid, 'private', 'data'), {
      email: userEmail,
      fechaNacimiento,
    });
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      displayName,
      username,
      pais,
      provincia,
      ciudad,
      photoURL: userPhotoURL,
      bio: '',
      following: [],
      followers: [],
      followersCount: 0,
      followingCount: 0,
      verified: false,
      createdAt: serverTimestamp(),
    });
  };

  // En la app de Android (WebView) el inicio de sesión con Google lo hace el selector de
  // cuentas nativo, que devuelve el ID token llamando a window.handleGoogleToken.
  useEffect(() => {
    // Sin token y sin error: el usuario cerró el selector de cuentas.
    window.handleGoogleToken = async (idToken, nativeError = null) => {
      if (!idToken) {
        setGoogleLoading(false);
        if (nativeError) setError('No se pudo iniciar sesión con Google. Intenta de nuevo');
        return;
      }
      try {
        await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      } catch {
        setError('No se pudo iniciar sesión con Google. Intenta de nuevo');
      } finally {
        setGoogleLoading(false);
      }
    };
    return () => { delete window.handleGoogleToken; };
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    if (typeof window.Android?.launchGoogleSignIn === 'function') {
      window.Android.launchGoogleSignIn();
      return;
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    // En la TWA de Android (y en la PWA instalada) la ventana emergente se abre en otra
    // pestaña que nunca devuelve el resultado a la app: se redirige la página completa.
    if (window.matchMedia?.('(display-mode: standalone)').matches || document.referrer.startsWith('android-app://')) {
      try {
        await signInWithRedirect(auth, provider);
      } catch {
        setError('No se pudo iniciar sesión con Google. Intenta de nuevo');
        setGoogleLoading(false);
      }
      return;
    }
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      // Algunos navegadores (y la app de Android) bloquean ventanas emergentes:
      // en ese caso se hace el inicio de sesión redirigiendo la página completa.
      if (err?.code === 'auth/popup-blocked' || err?.code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, provider);
        return;
      }
      if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
        setError('No se pudo iniciar sesión con Google. Intenta de nuevo');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (view === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else if (view === 'complete') {
        const validationError = validateProfileFields() || validateLegal();
        if (validationError) {
          setError(validationError);
          return;
        }
        await saveProfile(completeProfileFor, completeProfileFor.email, completeProfileFor.photoURL || null);
      } else if (view === 'register') {
        const fieldsError = validateProfileFields();
        if (fieldsError) {
          setError(fieldsError);
          return;
        }
        if (!isPasswordStrong) {
          setError('La contraseña no cumple los requisitos de seguridad');
          return;
        }
        if (password !== confirmPassword) {
          setError('Las contraseñas no coinciden');
          return;
        }
        const legalError = validateLegal();
        if (legalError) {
          setError(legalError);
          return;
        }
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        await saveProfile(user, email);
      } else {
        await sendPasswordResetEmail(auth, email);
        alert('Correo de recuperación enviado');
        setView('login');
      }
    } catch (err) {
      setError(view === 'complete'
        ? 'No se pudo guardar tu perfil. Intenta de nuevo'
        : 'Verifique sus credenciales e intente de nuevo');
    }
  };

  const inputBoxSx = {
    display: 'flex',
    alignItems: 'center',
    bgcolor: '#151c24',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.07)',
    px: 2,
    height: 52,
    transition: 'border-color 0.2s',
    '&:focus-within': {
      bgcolor: '#1a222d',
      borderColor: '#00d4ff',
    },
  };

  const autofillSx = {
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #151c24 inset',
      WebkitTextFillColor: '#fff',
      caretColor: '#fff',
      transition: 'background-color 9999s ease-in-out 0s',
    },
  };

  const plainInputSx = {
    flex: 1, border: 'none', outline: 'none',
    background: 'transparent', color: '#fff',
    fontSize: '0.93rem', fontFamily: 'inherit',
    '&::placeholder': { color: 'rgba(255,255,255,0.35)' },
    ...autofillSx,
  };

  return (
    // Fuera de sesión no hay datos que recargar: refrescar recarga la página entera.
    <PullToRefresh onRefresh={() => window.location.reload()} disabled={googleLoading}>
    <Box sx={{
      background: 'radial-gradient(ellipse at 50% 0%, #0d1f2d 0%, #000 65%)',
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2,
      fontFamily: '"Inter", "Helvetica Neue", sans-serif',
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0, md: 7 },
        width: '100%',
        maxWidth: 1000,
      }}>
        {/* Panel de marca - solo escritorio */}
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          flex: 1,
          maxWidth: 460,
        }}>
          <Box sx={{ position: 'relative', height: 340, mb: 5 }}>
            {[
              { rotate: '-9deg', top: 30, left: 10, w: 168, h: 260, accent: '#0e2a3a', icon: <ChatBubbleRounded sx={{ fontSize: 20, color: '#00d4ff' }} /> },
              { rotate: '6deg', top: 0, left: 130, w: 176, h: 280, accent: '#0a1420', icon: <Bolt sx={{ fontSize: 22, color: '#0055ff' }} />, front: true },
              { rotate: '-3deg', top: 55, left: 250, w: 160, h: 248, accent: '#0e2a3a', icon: <FavoriteRounded sx={{ fontSize: 18, color: '#ff4d8d' }} /> },
            ].map((card, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  top: card.top,
                  left: card.left,
                  width: card.w,
                  height: card.h,
                  borderRadius: '22px',
                  bgcolor: '#0b1219',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: card.front
                    ? '0 24px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,212,255,0.15)'
                    : '0 16px 32px rgba(0,0,0,0.45)',
                  transform: `rotate(${card.rotate})`,
                  zIndex: card.front ? 2 : 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ height: '38%', background: `linear-gradient(160deg, ${card.accent} 0%, #000 100%)` }} />
                <Stack spacing={1} sx={{ p: 1.6 }}>
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ width: '55%', height: 6, borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.1)' }} />
                  </Stack>
                  <Box sx={{ width: '80%', height: 6, borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.07)' }} />
                  <Box sx={{ width: '60%', height: 6, borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.07)' }} />
                </Stack>
                <Box sx={{ position: 'absolute', bottom: 14, right: 14, width: 34, height: 34, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {card.icon}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: '2.1rem', lineHeight: 1.25, color: '#fff', letterSpacing: '-0.5px', fontFamily: 'inherit' }}>
            Conectá con las conversaciones{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #00d4ff, #0055ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              que importan.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', mt: 1.5, fontFamily: 'inherit' }}>
            Gexvi es la red social del futuro.
          </Typography>
        </Box>

        <Container maxWidth="xs" disableGutters sx={{ flexShrink: 0 }}>
        <Box sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: '28px',
          bgcolor: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
          textAlign: 'center',
        }}>

          {/* Logo */}
          <Box component="img" src="/logo192.png" sx={{ height: 56, mb: 2.5, filter: 'drop-shadow(0 0 12px rgba(0,212,255,0.4))' }} />

          <Typography sx={{ fontWeight: 700, fontSize: '1.7rem', color: '#fff', letterSpacing: '-0.5px', mb: 0.5, fontFamily: 'inherit' }}>
            {view === 'login' ? 'Bienvenido'
              : view === 'register' ? 'Únete'
              : view === 'complete' ? 'Completa tu perfil'
              : 'Recuperar acceso'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', mb: 3.5, fontFamily: 'inherit' }}>
            {view === 'login' ? 'Ingresa a tu cuenta de Gexvi'
              : view === 'register' ? 'Crea tu perfil en la red del futuro'
              : view === 'complete' ? `Último paso para ${completeProfileFor?.email || 'tu cuenta de Google'}`
              : 'Te enviaremos un enlace de recuperación'}
          </Typography>

          {error && (
            <Box sx={{ bgcolor: 'rgba(255,75,75,0.1)', border: '1px solid rgba(255,75,75,0.3)', borderRadius: '10px', py: 1, px: 2, mb: 2 }}>
              <Typography sx={{ color: '#ff6b6b', fontSize: '0.82rem', fontFamily: 'inherit' }}>{error}</Typography>
            </Box>
          )}

          <form onSubmit={handleAuth}>
            <Stack spacing={1.5}>
              {/* Nombre para registro */}
              {isProfileForm && (
                <Box sx={inputBoxSx}>
                  <Box
                    component="input"
                    type="text"
                    placeholder="Nombre completo"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    sx={plainInputSx}
                  />
                </Box>
              )}

              {/* Nombre de usuario para registro */}
              {isProfileForm && (
                <Box sx={inputBoxSx}>
                  <Box
                    component="input"
                    type="text"
                    placeholder="Nombre de usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    sx={plainInputSx}
                  />
                </Box>
              )}

              {/* Email (la cuenta de Google ya trae el suyo) */}
              {view !== 'complete' && (
                <Box sx={inputBoxSx}>
                  <Box
                    component="input"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={plainInputSx}
                  />
                </Box>
              )}

              {/* Contraseña */}
              {(view === 'login' || view === 'register') && (
                <Box sx={inputBoxSx}>
                  <Box
                    component="input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    sx={plainInputSx}
                  />
                  <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5 }}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Box>
              )}

              {/* Medidor de seguridad + requisitos (solo registro) */}
              {view === 'register' && password.length > 0 && (
                <Box sx={{ textAlign: 'left', px: 0.5 }}>
                  <Stack direction="row" spacing={0.6} sx={{ mb: 0.6 }}>
                    {PASSWORD_REQUIREMENTS.map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: 1,
                          height: 4,
                          borderRadius: '999px',
                          bgcolor: index < passwordScore ? strengthInfo.color : 'rgba(255,255,255,0.1)',
                          transition: 'background-color 0.25s',
                        }}
                      />
                    ))}
                  </Stack>
                  {strengthInfo.label && (
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: strengthInfo.color, mb: 0.8 }}>
                      Seguridad: {strengthInfo.label}
                    </Typography>
                  )}
                  <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 0.4, columnGap: 1.5 }}>
                    {PASSWORD_REQUIREMENTS.map((req) => {
                      const met = passwordChecks[req.key];
                      return (
                        <Stack key={req.key} direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: '45%' }}>
                          {met ? (
                            <CheckCircle sx={{ fontSize: 14, color: '#00e676' }} />
                          ) : (
                            <RadioButtonUnchecked sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }} />
                          )}
                          <Typography sx={{ fontSize: '0.72rem', color: met ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.4)' }}>
                            {req.label}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Confirmar contraseña para registro */}
              {view === 'register' && (
                <Box>
                  <Box sx={inputBoxSx}>
                    <Box
                      component="input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirmar contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      sx={plainInputSx}
                    />
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5 }}>
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Box>
                  {!passwordsMatch && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#ff6b6b', mt: 0.5, textAlign: 'left', px: 0.5 }}>
                      Las contraseñas no coinciden
                    </Typography>
                  )}
                </Box>
              )}

              {/* País para registro */}
              {isProfileForm && (
                <Autocomplete
                  freeSolo
                  options={countries}
                  inputValue={pais}
                  onInputChange={(_e, newValue, reason) => {
                    setPais(newValue);
                    paisRef.current = newValue;
                    if (reason !== 'input') handleCountryChange(newValue);
                  }}
                  onBlur={() => handleCountryChange(paisRef.current)}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim().toLowerCase();
                    if (!input) return [];
                    return options.filter((c) => c.toLowerCase().includes(input)).slice(0, 8);
                  }}
                  slotProps={autocompleteSlotProps}
                  renderInput={(params) => (
                    <Box ref={params.InputProps.ref} sx={inputBoxSx}>
                      <Box
                        component="input"
                        {...params.inputProps}
                        placeholder={countriesLoading ? 'Cargando países...' : 'País'}
                        sx={plainInputSx}
                      />
                    </Box>
                  )}
                />
              )}

              {/* Provincia/Estado para registro (solo si el país tiene divisiones) */}
              {isProfileForm && pais && (statesLoading || requiresProvincia) && (
                <Autocomplete
                  freeSolo
                  options={states}
                  inputValue={provincia}
                  onInputChange={(_e, newValue, reason) => {
                    setProvincia(newValue);
                    provinciaRef.current = newValue;
                    if (reason !== 'input') handleProvinciaChange(newValue);
                  }}
                  onBlur={() => handleProvinciaChange(provinciaRef.current)}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim().toLowerCase();
                    if (!input) return [];
                    return options.filter((c) => c.toLowerCase().includes(input)).slice(0, 8);
                  }}
                  slotProps={autocompleteSlotProps}
                  renderInput={(params) => (
                    <Box ref={params.InputProps.ref} sx={inputBoxSx}>
                      <Box
                        component="input"
                        {...params.inputProps}
                        placeholder={statesLoading ? 'Cargando provincias...' : 'Provincia / Estado'}
                        sx={plainInputSx}
                      />
                    </Box>
                  )}
                />
              )}

              {/* Ciudad para registro */}
              {isProfileForm && pais && (
                <Autocomplete
                  freeSolo
                  options={cityOptions}
                  inputValue={ciudad}
                  onInputChange={(_e, newValue) => setCiudad(newValue)}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim().toLowerCase();
                    if (!input) return [];
                    return options.filter((c) => c.toLowerCase().includes(input)).slice(0, 8);
                  }}
                  slotProps={autocompleteSlotProps}
                  renderInput={(params) => (
                    <Box ref={params.InputProps.ref} sx={inputBoxSx}>
                      <Box
                        component="input"
                        {...params.inputProps}
                        placeholder={citiesLoading ? 'Cargando ciudades...' : 'Ciudad'}
                        sx={plainInputSx}
                      />
                    </Box>
                  )}
                />
              )}

              {isProfileForm && geoError && (
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', textAlign: 'left', px: 0.5, mt: -0.5 }}>
                  {geoError}
                </Typography>
              )}

              {/* Fecha de Nacimiento para registro */}
              {isProfileForm && (
                <Box sx={inputBoxSx}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      value={selectedBirthDate}
                      onChange={(newValue) => setFechaNacimiento(newValue ? format(newValue, DATE_FORMAT) : '')}
                      maxDate={maxBirthDate}
                      minDate={minBirthDate}
                      openTo="year"
                      views={['year', 'month', 'day']}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          placeholder: 'Fecha de nacimiento',
                          variant: 'standard',
                          fullWidth: true,
                          InputProps: { disableUnderline: true },
                          sx: {
                            '& .MuiInputBase-input': {
                              color: '#fff',
                              fontSize: '0.93rem',
                              fontFamily: 'inherit',
                              p: 0,
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 },
                          },
                        },
                        openPickerButton: { sx: { color: 'rgba(255,255,255,0.5)', mr: -1 } },
                        popper: {
                          sx: {
                            '& .MuiPaper-root': {
                              bgcolor: '#12181f',
                              color: '#fff',
                              border: '1px solid rgba(255,255,255,0.1)',
                            },
                            '& .MuiPickersCalendarHeader-label': { color: '#fff' },
                            '& .MuiPickersCalendarHeader-switchViewIcon': { color: '#fff' },
                            '& .MuiIconButton-root': { color: '#fff' },
                            '& .MuiDayCalendar-weekDayLabel': { color: 'rgba(255,255,255,0.5)' },
                            '& .MuiPickersDay-root': { color: '#fff' },
                            '& .MuiPickersDay-root.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
                            '& .MuiPickersDay-root.Mui-selected': { bgcolor: '#00d4ff', color: '#001217' },
                            '& .MuiPickersYear-yearButton': { color: '#fff' },
                            '& .MuiPickersYear-yearButton.Mui-selected': { bgcolor: '#00d4ff', color: '#001217' },
                            '& .MuiPickersMonth-monthButton': { color: '#fff' },
                            '& .MuiPickersMonth-monthButton.Mui-selected': { bgcolor: '#00d4ff', color: '#001217' },
                          },
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </Box>
              )}

              {isProfileForm && (
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', ml: 0.2, mr: 0 }}
                  control={
                    <Checkbox
                      checked={acceptedLegal}
                      onChange={(e) => setAcceptedLegal(e.target.checked)}
                      sx={{ color: 'rgba(255,255,255,0.55)', p: 0.6, mt: 0.15 }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', lineHeight: 1.5, textAlign: 'left' }}>
                      Acepto la{' '}
                      <Link href="/politica_de_privacidad.html" target="_blank" rel="noopener noreferrer" sx={linkStyle}>
                        Política de Privacidad
                      </Link>
                      , los{' '}
                      <Link href="/terminos_y_condiciones.html" target="_blank" rel="noopener noreferrer" sx={linkStyle}>
                        Términos y Condiciones
                      </Link>
                      {' '}y los{' '}
                      <Link href="/estandares-csae.html" target="_blank" rel="noopener noreferrer" sx={linkStyle}>
                        Estándares CSAE
                      </Link>
                      .
                    </Typography>
                  }
                />
              )}

              <Button
                fullWidth type="submit" variant="contained"
                sx={{
                  py: 1.4, borderRadius: '14px', fontWeight: 600,
                  background: 'linear-gradient(90deg, #00d4ff 0%, #0055ff 100%)',
                  textTransform: 'none', fontSize: '0.95rem',
                  fontFamily: 'inherit', letterSpacing: '0.2px',
                  boxShadow: '0 4px 20px rgba(0,180,255,0.3)',
                  '&:hover': { boxShadow: '0 6px 24px rgba(0,180,255,0.45)' },
                  mt: 0.5,
                }}
              >
                {view === 'login' ? 'Entrar'
                  : view === 'register' ? 'Registrarme'
                  : view === 'complete' ? 'Crear perfil'
                  : 'Enviar enlace'}
              </Button>
            </Stack>
          </form>

          {(view === 'login' || view === 'register') && (
            <>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', fontFamily: 'inherit' }}>o</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Stack>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                startIcon={<GoogleLogo />}
                sx={{
                  py: 1.3, borderRadius: '14px', fontWeight: 600,
                  textTransform: 'none', fontSize: '0.93rem', fontFamily: 'inherit',
                  color: '#fff', bgcolor: '#151c24',
                  borderColor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: '#1a222d', borderColor: 'rgba(255,255,255,0.25)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Continuar con Google
              </Button>
            </>
          )}

          {/* Links */}
          <Stack spacing={0.8} sx={{ mt: 2.5, alignItems: 'center' }}>
            {view === 'login' ? (
              <>
                <Link onClick={() => setView('forgot')} sx={linkStyle}>¿Olvidaste tu contraseña?</Link>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', fontFamily: 'inherit', mt: 0.5 }}>
                  ¿No tienes cuenta?{' '}
                  <Link onClick={() => setView('register')} sx={linkStyle}>Regístrate</Link>
                </Typography>
              </>
            ) : view === 'complete' ? (
              <Link onClick={() => signOut(auth)} sx={linkStyle}>Usar otra cuenta</Link>
            ) : (
              <Button
                startIcon={<ArrowBackIosNew sx={{ fontSize: '0.75rem !important' }} />}
                onClick={() => setView('login')}
                sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontFamily: 'inherit', fontSize: '0.85rem' }}
              >
                Volver al inicio
              </Button>
            )}
          </Stack>
        </Box>
        </Container>
      </Box>
    </Box>
    </PullToRefresh>
  );
};

const linkStyle = {
  color: '#00d4ff', textDecoration: 'none', cursor: 'pointer',
  fontSize: '0.85rem', fontFamily: '"Inter", "Helvetica Neue", sans-serif',
  '&:hover': { textDecoration: 'underline' },
};

export default Auth;
